import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { userService } from "../services/api";

export const AdminPage = () => {
  const navigate = useNavigate();

  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const [form, setForm] = useState({
    firstName: "",
    lastName: "",
    email: "",
    password: "",
    dni: "",
    role: "referente",
  });
  const [formError, setFormError] = useState("");
  const [creating, setCreating] = useState(false);
  const [filterRole, setFilterRole] = useState("todos");

  // Estado para edicion
  const [editUser, setEditUser] = useState(null);
  const [editForm, setEditForm] = useState({
    firstName: "",
    lastName: "",
    email: "",
    dni: "",
    role: "",
  });
  const [editError, setEditError] = useState("");
  const [saving, setSaving] = useState(false);

  const loadUsers = async () => {
    try {
      setLoading(true);
      const res = await userService.getAllUsers(1, 100);
      const data = res.data;
      const list = data.users || data;
      const filtered = Array.isArray(list)
        ? list.filter(
            (u) =>
              u.role?.name === "referente" || u.role?.name === "secretaria",
          )
        : [];
      setUsers(filtered);
    } catch {
      setError("Error al cargar los usuarios");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadUsers();
  }, []);

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
    setFormError("");
    setSuccess("");
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    setFormError("");
    setSuccess("");
    if (!form.firstName || !form.lastName || !form.email || !form.password) {
      setFormError("Nombre, apellido, email y contrasena son obligatorios");
      return;
    }
    setCreating(true);
    try {
      await userService.createManagedUser(form);
      const roleLabel = form.role === "secretaria" ? "Secretaria" : "Referente";
      setSuccess(`${roleLabel} creado/a correctamente`);
      setForm({
        firstName: "",
        lastName: "",
        email: "",
        password: "",
        dni: "",
        role: "referente",
      });
      await loadUsers();
    } catch (err) {
      setFormError(err.response?.data?.message || "Error al crear el usuario");
    } finally {
      setCreating(false);
    }
  };

  const handleEditOpen = (u) => {
    setEditUser(u);
    setEditForm({
      firstName: u.firstName,
      lastName: u.lastName,
      email: u.email,
      dni: u.dni || "",
      role: u.role?.name || "referente",
    });
    setEditError("");
  };

  const handleEditSave = async () => {
    if (!editForm.firstName || !editForm.lastName || !editForm.email) {
      setEditError("Nombre, apellido y email son obligatorios");
      return;
    }
    setSaving(true);
    try {
      await userService.updateUser(editUser.id, editForm);
      setSuccess(
        `Usuario ${editForm.firstName} ${editForm.lastName} actualizado`,
      );
      setEditUser(null);
      await loadUsers();
    } catch (err) {
      setEditError(err.response?.data?.message || "Error al guardar");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (userId, name) => {
    if (!window.confirm(`Eliminar a ${name}?`)) return;
    setError("");
    setSuccess("");
    try {
      await userService.deleteUser(userId);
      setSuccess(`Usuario ${name} eliminado`);
      await loadUsers();
    } catch (err) {
      setError(err.response?.data?.message || "Error al eliminar");
    }
  };

  const displayedUsers =
    filterRole === "todos"
      ? users
      : users.filter((u) => u.role?.name === filterRole);

  return (
    <>
      <main className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="flex items-center mb-6">
          <h1 className="text-2xl font-bold text-slate-800">Administrar Usuarios</h1>
          <button onClick={() => navigate("/dashboard")} className="btn-secondary ml-auto py-1.5 px-4 text-sm">
            Volver al Dashboard
          </button>
        </div>

        {/* Formulario creación */}
        <div className="card p-6 mb-6">
          <h2 className="text-lg font-semibold text-slate-800 mb-4">Crear nuevo usuario</h2>
          {formError && (
            <div className="mb-4 rounded-md bg-red-50 border border-red-200 px-4 py-3 text-sm text-red-700">{formError}</div>
          )}
          {success && (
            <div className="mb-4 rounded-md bg-green-50 border border-green-200 px-4 py-3 text-sm text-green-700">{success}</div>
          )}
          <form onSubmit={handleCreate}>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="label">Nombre *</label>
                <input type="text" name="firstName" value={form.firstName} onChange={handleChange} placeholder="Nombre" className="input" />
              </div>
              <div>
                <label className="label">Apellido *</label>
                <input type="text" name="lastName" value={form.lastName} onChange={handleChange} placeholder="Apellido" className="input" />
              </div>
              <div>
                <label className="label">Email *</label>
                <input type="email" name="email" value={form.email} onChange={handleChange} placeholder="correo@ejemplo.com" className="input" />
              </div>
              <div>
                <label className="label">Contraseña *</label>
                <input type="password" name="password" value={form.password} onChange={handleChange} placeholder="Contraseña" className="input" />
              </div>
              <div>
                <label className="label">DNI (opcional)</label>
                <input type="text" name="dni" value={form.dni} onChange={handleChange} placeholder="DNI" className="input" />
              </div>
              <div>
                <label className="label">Rol *</label>
                <select name="role" value={form.role} onChange={handleChange} className="input">
                  <option value="referente">Referente</option>
                  <option value="secretaria">Secretaria</option>
                </select>
              </div>
            </div>
            <button type="submit" disabled={creating} className="btn-primary mt-4">
              {creating ? "Creando..." : "Crear Usuario"}
            </button>
          </form>
        </div>

        {/* Lista usuarios */}
        <div className="card overflow-hidden">
          <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200">
            <h2 className="text-lg font-semibold text-slate-800">Usuarios del sistema</h2>
            <select
              value={filterRole}
              onChange={(e) => setFilterRole(e.target.value)}
              className="input py-1.5 w-auto min-w-[150px]"
            >
              <option value="todos">Todos</option>
              <option value="referente">Solo referentes</option>
              <option value="secretaria">Solo secretarias</option>
            </select>
          </div>

          {error && (
            <div className="mx-6 mt-4 rounded-md bg-red-50 border border-red-200 px-4 py-3 text-sm text-red-700">{error}</div>
          )}

          {loading ? (
            <div className="flex justify-center py-12">
              <svg className="animate-spin h-7 w-7 text-primary-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z" />
              </svg>
            </div>
          ) : displayedUsers.length === 0 ? (
            <p className="text-slate-500 px-6 py-8 text-center">No hay usuarios para mostrar.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-slate-200 text-sm">
                <thead className="bg-slate-50">
                  <tr>
                    {["Nombre", "Email", "DNI", "Rol", "Estado", ""].map((h) => (
                      <th key={h} className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wide">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100 bg-white">
                  {displayedUsers.map((u) => (
                    <tr key={u.id} className="hover:bg-slate-50">
                      <td className="px-4 py-3 font-medium text-slate-800">{u.firstName} {u.lastName}</td>
                      <td className="px-4 py-3 text-slate-500">{u.email}</td>
                      <td className="px-4 py-3 text-slate-500">{u.dni || "-"}</td>
                      <td className="px-4 py-3">
                        <span className={`badge ${u.role?.name === "secretaria" ? "bg-blue-100 text-blue-700" : "bg-green-100 text-green-700"}`}>
                          {u.role?.name === "secretaria" ? "Secretaria" : "Referente"}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <span className={`badge ${u.isActive ? "bg-green-100 text-green-700" : "bg-red-100 text-red-700"}`}>
                          {u.isActive ? "Activo" : "Inactivo"}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-right">
                        <div className="flex gap-2 justify-end">
                          <button onClick={() => handleEditOpen(u)} className="btn-primary py-1 px-3 text-xs">Editar</button>
                          <button onClick={() => handleDelete(u.id, `${u.firstName} ${u.lastName}`)} className="btn-danger py-1 px-3 text-xs">Eliminar</button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </main>

      {/* Modal edición */}
      {editUser && (
        <div
          className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
          onClick={(e) => { if (e.target === e.currentTarget) setEditUser(null); }}
        >
          <div className="bg-white rounded-xl shadow-2xl p-7 w-full max-w-md">
            <h2 className="text-lg font-semibold text-slate-800 mb-5">Editar usuario</h2>
            {editError && (
              <div className="mb-4 rounded-md bg-red-50 border border-red-200 px-4 py-3 text-sm text-red-700">{editError}</div>
            )}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="label">Nombre *</label>
                <input type="text" value={editForm.firstName} onChange={(e) => setEditForm({ ...editForm, firstName: e.target.value })} className="input" />
              </div>
              <div>
                <label className="label">Apellido *</label>
                <input type="text" value={editForm.lastName} onChange={(e) => setEditForm({ ...editForm, lastName: e.target.value })} className="input" />
              </div>
              <div className="col-span-2">
                <label className="label">Email *</label>
                <input type="email" value={editForm.email} onChange={(e) => setEditForm({ ...editForm, email: e.target.value })} className="input" />
              </div>
              <div className="col-span-2">
                <label className="label">DNI</label>
                <input type="text" value={editForm.dni} onChange={(e) => setEditForm({ ...editForm, dni: e.target.value })} placeholder="Opcional" className="input" />
              </div>
              <div className="col-span-2">
                <label className="label">Rol *</label>
                <select value={editForm.role} onChange={(e) => setEditForm({ ...editForm, role: e.target.value })} className="input">
                  <option value="referente">Referente</option>
                  <option value="secretaria">Secretaria</option>
                </select>
              </div>
            </div>
            <div className="flex gap-3 mt-6 justify-end">
              <button onClick={() => setEditUser(null)} className="btn-secondary">Cancelar</button>
              <button onClick={handleEditSave} disabled={saving} className="btn-primary">
                {saving ? "Guardando..." : "Guardar cambios"}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};
